import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Upload, Database, AlertTriangle, CheckCircle, FileJson, ArrowRight, ShieldCheck, RefreshCw, HardDrive, History, Trash2 } from "lucide-react";
import { db, auth, appId } from "../firebase";
import { collection, getDocs, writeBatch, doc } from "firebase/firestore";
import { useModal } from "../contexts/ModalContext";

// [3D Icon Assets]
const ICONS = {
  backup: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/File%20Folder.png", // Folder for Backup
  restore: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Card%20Index%20Dividers.png", // Index for Restore
  reset: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Cross%20Mark.png", // X for Reset
  security: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Locked.png",
  check: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Check%20Mark%20Button.png",
  warning: "https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Symbols/Warning.png",
};

// [Component] Premium Glass Card
const GlassCard = ({ children, className, onClick }) => (
  <div onClick={onClick} className={`
      relative overflow-hidden rounded-[2.5rem] 
      bg-white/60 backdrop-blur-3xl 
      border border-white/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]
      hover:shadow-2xl hover:-translate-y-1 hover:border-white/60 hover:bg-white/70
      transition-all duration-500 ease-out
      group
      ${className}
  `}>
    <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none"></div>
    <div className="relative z-10 h-full">{children}</div>
  </div>
);

export default function DataMigrationView() {
  const [isLoading, setIsLoading] = useState(false);
  const [log, setLog] = useState("");
  const { openConfirm, openAlert } = useModal();

  // 1. 데이터 내보내기 (다운로드)
  const handleExport = async () => {
    if (!auth.currentUser) return;

    openConfirm(
      "현재 저장된 모든 데이터를 파일로 내려받으시겠습니까?\n생성된 백업 파일은 안전한 곳에 보관해주세요.",
      "데이터 백업",
      async () => {
        setIsLoading(true);
        setLog("📦 데이터를 포장하는 중입니다...");

        try {
          // [MOD] Read from root collections with userId filter
          const { query, where, getDocs: getDocsQuery } = await import("firebase/firestore");
          const collections = ["students", "teachers", "finances", "schedules", "visitLogs", "meetingMinutes"];
          const exportData = {};

          for (const colName of collections) {
            setLog(`📂 '${colName}' 데이터를 수집 중...`);
            const colRef = collection(db, colName);
            const q = query(colRef, where("userId", "==", auth.currentUser.uid));
            const snapshot = await getDocsQuery(q);
            exportData[colName] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          }

          // Also export attendance from artifacts path
          setLog(`📂 'attendance' 데이터를 수집 중...`);
          const attendanceRef = collection(db, "artifacts", appId, "users", auth.currentUser.uid, "attendance");
          const attendanceSnapshot = await getDocs(attendanceRef);
          exportData["attendance"] = attendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          const jsonString = JSON.stringify(exportData, null, 2);
          const blob = new Blob([jsonString], { type: "application/json" });
          const href = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = href;
          link.download = `THESTAFF_BACKUP_${new Date().toISOString().slice(0, 10)}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setLog("✅ 백업 파일이 성공적으로 생성되었습니다.");
        } catch (error) {
          console.error(error);
          setLog("❌ 내보내기 실패: " + error.message);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  // 2. 데이터 가져오기 (업로드)
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const importData = JSON.parse(event.target.result);
        openConfirm(
          "데이터 복구 시작",
          "현재 데이터에 백업 파일의 내용이 덮어씌워지거나 추가됩니다.\n정말 진행하시겠습니까?",
          async () => {
            setIsLoading(true);
            setLog("🔍 데이터 무결성 검사 중...");

            try {
              const batch = writeBatch(db);
              let count = 0;

              for (const [colName, items] of Object.entries(importData)) {
                if (!Array.isArray(items)) continue;

                setLog(`↺ '${colName}' 복원 중...`);
                for (const item of items) {
                  const docRef = doc(db, "artifacts", appId, "users", auth.currentUser.uid, colName, String(item.id));
                  batch.set(docRef, item);
                  count++;
                }
              }

              setLog(`💾 데이터베이스에 기록 중... (총 ${count}개 항목)`);
              await batch.commit();
              setLog(`🎉 복구 완료! 총 ${count}개의 데이터가 복원되었습니다.`);

              setTimeout(() => {
                openAlert("데이터 복구가 완료되었습니다.\n최신 상태 반영을 위해 앱을 새로고침합니다.", "복구 성공", () => {
                  window.location.reload();
                });
              }, 1000);

            } catch (error) {
              console.error(error);
              setLog("❌ 가져오기 실패: 데이터베이스 오류가 발생했습니다.");
            } finally {
              setIsLoading(false);
            }
          }
        );
      } catch (err) {
        openAlert("올바른 JSON 백업 파일이 아닙니다.", "파일 오류");
        setLog("❌ 파일 형식 오류");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // 3. 데이터 초기화 (Reset) with Confirmation Input
  const handleReset = () => {
    if (!auth.currentUser) return;

    // Custom Modal implementation or simply using browser prompt/confirm for strictness if modal context doesn't support input
    // Assuming useModal only supports simple confirm. Let's use a native prompt for maximum safety and straightforwardness, 
    // OR implementing a custom UI state here. Let's use a custom UI state for "Confirm Input".

    // Actually, let's use the browser's `prompt` for the text input to ensure it blocks and is distinct.
    // If we want a nicer UI, we should add a state for "isResetModalOpen".
    // Let's go with a nicer custom UI overlay within this component for the Reset Confirmation.
    setIsResetModalOpen(true);
    setResetInputValue("");
  };

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetInputValue, setResetInputValue] = useState("");
  const CONFIRM_TEXT = "데이터 초기화";

  const executeReset = async () => {
    if (resetInputValue !== CONFIRM_TEXT) return;
    setIsResetModalOpen(false);
    setIsLoading(true);
    setLog("⚠️ 데이터 초기화 시작...");

    try {
      // [MOD] Delete from root collections - try both userId and createdBy
      const collections = ["students", "teachers", "finances", "schedules", "visitLogs", "meetingMinutes"];
      let totalDeleted = 0;
      const { query, where, getDocs: getDocsQuery, updateDoc, deleteField } = await import("firebase/firestore");

      for (const colName of collections) {
        setLog(`🗑️ '${colName}' 삭제 중...`);

        // Try deleting by userId
        try {
          const colRef = collection(db, colName);
          const qByUserId = query(colRef, where("userId", "==", auth.currentUser.uid));
          const snapshotByUserId = await getDocsQuery(qByUserId);

          if (snapshotByUserId.docs.length > 0) {
            const batch1 = writeBatch(db);
            snapshotByUserId.docs.forEach((docSnap) => {
              batch1.delete(docSnap.ref);
              totalDeleted++;
            });
            await batch1.commit();
          }
        } catch (e) {
          console.log(`${colName} userId 삭제 스킵:`, e.message);
        }

        // Also try deleting by createdBy
        try {
          const colRef = collection(db, colName);
          const qByCreatedBy = query(colRef, where("createdBy", "==", auth.currentUser.uid));
          const snapshotByCreatedBy = await getDocsQuery(qByCreatedBy);

          if (snapshotByCreatedBy.docs.length > 0) {
            const batch2 = writeBatch(db);
            snapshotByCreatedBy.docs.forEach((docSnap) => {
              batch2.delete(docSnap.ref);
              totalDeleted++;
            });
            await batch2.commit();
          }
        } catch (e) {
          console.log(`${colName} createdBy 삭제 스킵:`, e.message);
        }
      }

      // Delete attendance from artifacts path
      setLog(`🗑️ 'attendance' 삭제 중...`);
      try {
        const attendanceRef = collection(db, "artifacts", appId, "users", auth.currentUser.uid, "attendance");
        const attendanceSnapshot = await getDocs(attendanceRef);
        if (attendanceSnapshot.docs.length > 0) {
          const attendanceBatch = writeBatch(db);
          attendanceSnapshot.docs.forEach((docSnap) => {
            attendanceBatch.delete(docSnap.ref);
            totalDeleted++;
          });
          await attendanceBatch.commit();
        }
      } catch (e) {
        console.log("attendance 삭제 스킵:", e.message);
      }

      // [NEW] Delete organization attendance if organizationId exists
      // getDoc을 사용해서 직접 사용자 문서 가져오기 (쿼리 대신)
      const { getDoc: getDocument, setDoc: setDocument } = await import("firebase/firestore");
      const userDocRef = doc(db, "users", auth.currentUser.uid);
      const userDocSnap = await getDocument(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : null;

      if (userData?.organizationId) {
        setLog(`🗑️ 조직 출석 데이터 삭제 중...`);
        try {
          const orgAttendanceRef = collection(db, "organizations", userData.organizationId, "attendance");
          const orgAttendanceSnapshot = await getDocs(orgAttendanceRef);
          if (orgAttendanceSnapshot.docs.length > 0) {
            const orgBatch = writeBatch(db);
            orgAttendanceSnapshot.docs.forEach((docSnap) => {
              orgBatch.delete(docSnap.ref);
              totalDeleted++;
            });
            await orgBatch.commit();
          }
        } catch (e) {
          // Ignore if no permission
          console.log("조직 출석 삭제 스킵:", e.message);
        }
      }

      // [NEW] Delete settings document entirely
      setLog(`🗑️ 설정 데이터 삭제 중...`);
      try {
        const { deleteDoc: deleteDocument } = await import("firebase/firestore");
        await deleteDocument(doc(db, "settings", auth.currentUser.uid));
      } catch (e) {
        // Settings doc might not exist
      }

      // [NEW] Clear localStorage chat history
      localStorage.removeItem(`chatHistory_${auth.currentUser.uid}`);

      // [NEW] Reset organization settings for fresh start
      setLog(`🔄 계정 설정 초기화 중...`);
      try {
        // 먼저 updateDoc 시도
        await updateDoc(userDocRef, {
          organizationId: deleteField(),
          memberRole: deleteField(),
          adminInviteCode: deleteField(),
          teacherInviteCode: deleteField(),
          myDepartments: deleteField(),
          deptName: deleteField(),
          deptColor: deleteField(),
          assignedDepartments: deleteField(),
          canManageFinance: deleteField(),
          setupComplete: false
        });
      } catch (updateError) {
        console.log("updateDoc 실패, setDoc으로 재시도:", updateError.message);
        // updateDoc 실패 시 setDoc으로 기본 정보만 유지하며 덮어쓰기
        await setDocument(userDocRef, {
          email: auth.currentUser.email,
          displayName: auth.currentUser.displayName || "",
          photoURL: auth.currentUser.photoURL || "",
          setupComplete: false,
          createdAt: userData?.createdAt || new Date().toISOString()
        });
      }

      setLog(`✨ 초기화 완료. 총 ${totalDeleted}개의 항목이 삭제되었습니다.`);

      setTimeout(() => {
        openAlert("모든 데이터가 안전하게 초기화되었습니다.\n앱을 새로고침합니다.", "초기화 완료", () => {
          window.location.reload();
        });
      }, 1500);

    } catch (error) {
      console.error(error);
      setLog("❌ 초기화 실패: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-10 pb-32 animate-fade-in text-gray-800">

      {/* [0] 타이틀 헤더 (Header Section) */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-6">
        <div>
          <h2 className="text-3xl font-black text-[#2C3E30] flex items-center gap-3 font-do-hyeon">
            <img src="/images/Card_file_box.png" width={48} alt="icon" className="drop-shadow-md" /> 데이터 센터
          </h2>
          <p className="text-gray-500 text-sm font-medium mt-1 ml-1">안전하게 백업하고, 언제든 복구하고, 필요하면 초기화하세요.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* 1. Export (Backup) Card */}
        <GlassCard id="tour-backup-card" className="group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-blue-200/50 transition-colors duration-700"></div>
          <div className="p-8 flex flex-col items-center text-center h-full relative z-10">
            <div className="mb-6 relative">
              <div className="absolute inset-0 bg-blue-400/20 blur-2xl rounded-full scale-150 animate-pulse-slow"></div>
              <img src={ICONS.backup} alt="Backup" className="w-24 h-24 object-contain drop-shadow-2xl transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" />
            </div>
            <h3 className="text-xl font-black text-gray-800 mb-2 font-do-hyeon">데이터 백업</h3>
            <p className="text-gray-500 text-xs font-medium leading-relaxed mb-6 break-keep">
              모든 데이터를 <span className="text-blue-600 font-bold">파일로 저장</span>합니다.
            </p>
            <div className="mt-auto w-full">
              <button onClick={handleExport} disabled={isLoading} className="w-full py-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 group-hover:bg-blue-600 group-hover:text-white">
                <Download size={18} /> 백업 파일 생성
              </button>
            </div>
          </div>
        </GlassCard>

        {/* 2. Import (Restore) Card */}
        <GlassCard id="tour-restore-card" className="group relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-emerald-200/50 transition-colors duration-700"></div>
          <div className="p-8 flex flex-col items-center text-center h-full relative z-10">
            <div className="mb-6 relative">
              <div className="absolute inset-0 bg-emerald-400/20 blur-2xl rounded-full scale-150 animate-pulse-slow"></div>
              <img src={ICONS.restore} alt="Restore" className="w-24 h-24 object-contain drop-shadow-2xl transform transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3" />
            </div>
            <h3 className="text-xl font-black text-gray-800 mb-2 font-do-hyeon">데이터 복구</h3>
            <p className="text-gray-500 text-xs font-medium leading-relaxed mb-6 break-keep">
              백업 파일로 <span className="text-emerald-600 font-bold">이전 상태로 복원</span>합니다.
            </p>
            <div className="mt-auto w-full">
              <label className="w-full py-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer group-hover:bg-emerald-600 group-hover:text-white">
                <Upload size={18} /> 백업 파일 선택
                <input type="file" accept=".json" className="hidden" onChange={handleImport} disabled={isLoading} />
              </label>
            </div>
          </div>
        </GlassCard>

        {/* 3. Reset (Initialization) Card - WARNING THEME */}
        <GlassCard className="group relative overflow-hidden border-rose-200">
          <div className="absolute top-0 right-0 w-64 h-64 bg-rose-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-rose-200/50 transition-colors duration-700"></div>
          <div className="p-8 flex flex-col items-center text-center h-full relative z-10">
            <div className="mb-6 relative">
              <div className="absolute inset-0 bg-rose-400/20 blur-2xl rounded-full scale-150 animate-pulse-slow"></div>
              <img src={ICONS.reset} alt="Reset" className="w-24 h-24 object-contain drop-shadow-2xl transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6" />
            </div>
            <h3 className="text-xl font-black text-rose-600 mb-2 font-do-hyeon">데이터 초기화</h3>
            <p className="text-gray-500 text-xs font-medium leading-relaxed mb-6 break-keep">
              서버에 저장된 <span className="text-rose-600 font-bold">나의 모든 데이터</span>를<br />영구적으로 삭제합니다.
            </p>
            <div className="mt-auto w-full">
              <button
                onClick={handleReset}
                disabled={isLoading}
                className="w-full py-3 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 group-hover:bg-rose-600 group-hover:text-white hover:shadow-lg hover:shadow-rose-500/30"
              >
                <Trash2 size={18} /> 데이터 초기화
              </button>
            </div>
          </div>
        </GlassCard>

      </div>

      {/* System Log Console */}
      <GlassCard className="p-0 overflow-hidden bg-gray-900/95 backdrop-blur-md border-gray-800/50">
        <div className="bg-gray-800/80 px-6 py-3 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
            <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
            <span className="ml-3 text-xs font-mono text-gray-400 font-bold">SYSTEM_LOG_MONITOR</span>
          </div>
          <ShieldCheck size={16} className="text-emerald-500/70" />
        </div>
        <div className="p-6 h-32 flex items-center justify-center bg-gray-900/50 font-mono">
          {log ? (
            <div className={`text-sm md:text-base font-bold animate-fade-in-up flex items-center gap-3 ${log.includes("실패") || log.includes("오류") ? "text-red-400" : log.includes("초기화") ? "text-yellow-400" : "text-emerald-400"}`}>
              {log.includes("중...") && <RefreshCw size={18} className="animate-spin" />}
              {log.includes("완료") && <CheckCircle size={18} />}
              {log.includes("실패") && <AlertTriangle size={18} />}
              {log}
            </div>
          ) : (
            <span className="text-gray-600 text-sm animate-pulse">Waiting for user command...</span>
          )}
        </div>
      </GlassCard>

      <div className="flex items-center justify-center gap-2 text-gray-400 text-xs font-medium">
        <HardDrive size={12} />
        <span>데이터는 SSL 보안 연결을 통해 안전하게 처리됩니다.</span>
      </div>

      {/* Reset Confirmation Modal */}
      {isResetModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 text-center" onClick={() => setIsResetModalOpen(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl space-y-6 animate-fade-in-up" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto text-rose-500">
              <AlertTriangle size={32} />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-gray-800 font-do-hyeon">정말 초기화 하시겠습니까?</h3>
              <p className="text-gray-500 text-sm break-keep leading-relaxed">
                삭제된 데이터는 <span className="text-rose-600 font-bold">절대 복구할 수 없습니다.</span><br />
                진행하시려면 아래 입력창에<br />
                <span className="inline-block bg-gray-100 px-2 py-1 rounded text-gray-800 font-bold mt-1">데이터 초기화</span><br />
                를 정확히 입력해주세요.
              </p>
            </div>

            <input
              type="text"
              value={resetInputValue}
              onChange={(e) => setResetInputValue(e.target.value)}
              placeholder="데이터 초기화"
              className="w-full text-center py-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:ring-4 focus:ring-rose-500/20 outline-none font-bold text-gray-800 transition-all placeholder:font-normal placeholder:text-gray-300"
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={() => setIsResetModalOpen(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold transition-colors"
              >
                취소
              </button>
              <button
                onClick={executeReset}
                disabled={resetInputValue !== CONFIRM_TEXT}
                className="flex-1 py-3 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all shadow-lg shadow-rose-500/30"
              >
                초기화 실행
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
